import dayjs from 'dayjs'
import { html, LitElement, PropertyValues } from 'lit'
import { property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { when } from 'lit/directives/when.js'
import { concat, exhaustMap, finalize, map, merge, of, Subject, switchMap, takeUntil, tap, timer } from 'rxjs'
import { componentText, HostAriaLabelMixin, schmancyElement, SchmancyFormField } from '@schmancy/mixins/index'
import { show } from '@schmancy/overlay/overlay.service'
import { inExemptLayer } from '@schmancy/overlay/overlay.stack'
import { formatDateInstant } from '../date-format'
// Registering side-effect imports — a type-only import is elided by the
// compiler, which leaves the tag this template renders undefined.
import './date-field-dialog'
import '@schmancy/primitives/button/button'
import '@schmancy/primitives/typography/typography'

export type SchmancyDateFieldInputEvent = CustomEvent<{ value: string }>
export type SchmancyDateFieldChangeEvent = CustomEvent<{ value: string }>

/**
 * A single-instant date selector whose trigger opens one panel holding a DRAFT:
 * a draft bar, `<schmancy-calendar mode="single">`, `<schmancy-time-picker>`
 * under `datetime-local`, and a fixed Cancel/Save row. Nothing the user touches
 * in there reaches this field until Save, so `change` fires once per commit
 * rather than once per touch, and a stray click outside leaves the value alone.
 *
 * The trigger's label resolves through `formatDateInstant` — the same
 * `Intl.DateTimeFormat(this.locale, …)` core `<schmancy-date-range>` resolves
 * through — so the theme's locale, not the browser's, decides segment order and
 * the 12/24-hour convention, and a lone instant beside a range on one surface
 * reads in one format rather than two. A bare `<schmancy-input type="date">`
 * cannot hold that agreement: its native field follows the browser.
 *
 * `value` is a naive local wall-clock string (`YYYY-MM-DD` or
 * `YYYY-MM-DDTHH:mm`) — no timezone or offset is attached, read, or reconciled.
 * A value round-trips exactly as typed; there is no DST or cross-timezone
 * handling, by design.
 *
 * @element schmancy-date-field
 * @fires input  - Every draft move inside the panel, committed to nothing { value }
 * @fires change - Committed value change, on Save only                    { value }
 */
@schmancyElement('schmancy-date-field')
export class SchmancyDateField extends HostAriaLabelMixin(SchmancyFormField()) {
	// `name`, `label`, `disabled`, `required`, `readonly`, `error`,
	// `validationMessage`, `hint`, `id`, `validateOn`, `touched`, `submitted`,
	// FACE wiring and the FIELD_CONNECT_EVENT dispatch all come from the mixin.

	// Delegate focus into the trigger — without this the host itself has no
	// tabindex, so `.focus()` and a `<label for="...">` click both land nowhere.
	protected static shadowRootOptions = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true,
	}

	/** Narrows the mixin's `FormFieldValue` union to the one shape this control
	 *  holds, the same way `<schmancy-input>` does. */
	@property({ type: String, reflect: true }) override value = ''

	@property() type: 'date' | 'datetime-local' = 'date'
	@property() min?: string
	@property() max?: string

	/** The trigger's register, forwarded to the button it renders. `outlined` is
	 *  a padded control on a form of its own. `plain` is the inline trigger a
	 *  date edited inside a row the layout already sized needs — zero padding, no
	 *  control-height floor, sitting at its neighbour's baseline, which is what
	 *  `<schmancy-typography-field>` already does for text in that same row. */
	@property() variant: 'outlined' | 'text' | 'plain' = 'outlined'

	/** The trigger's ink, forwarded to the same button. `neutral` is what a
	 *  `plain` trigger in a rail takes, where the tint that marks a control on a
	 *  form of its own would make this one date the loudest value in the column. */
	@property() color: 'primary' | 'neutral' = 'primary'

	/** The `Intl.DateTimeFormatOptions` the trigger label and its announcement
	 *  render through, under the theme locale — `{ day: 'numeric', month: 'short',
	 *  year: 'numeric' }` reads "31 Dec 2027" in en-GB. It replaces the default
	 *  options outright, so under `datetime-local` the time shows only when these
	 *  options ask for it. Unset, the label keeps the format `<schmancy-date-range>`
	 *  shares. `timeZone` is excluded: `value` is naive wall-clock time, and a zone
	 *  would shift the rendered day away from the one stored. */
	@property({ attribute: false }) format?: Omit<Intl.DateTimeFormatOptions, 'timeZone'>

	#placeholder: string | null = null

	/** Trigger label while no date is set. Left alone it is the
	 *  `dateField.placeholder` wording, resolved on every read rather than frozen
	 *  at construction; a value assigned here wins over it. */
	@property()
	get placeholder(): string {
		return this.#placeholder ?? componentText('dateField.placeholder')
	}
	set placeholder(value: string) {
		this.#placeholder = value
	}

	/** What this control is CALLED, wherever a name is owed — the trigger's
	 *  composed name and the panel's own dialog name alike. `aria-label` first,
	 *  because naming is the narrower intent and a consumer reaching for it has
	 *  already said it wants no visible label; then `label`; then the same
	 *  wording an empty trigger shows, resolved on every read rather than frozen,
	 *  exactly as `placeholder` above is. */
	private get accessibleName(): string {
		return this.ariaLabel || this.label || componentText('dateField.placeholder')
	}

	// Session subjects — ONE_PIPELINE_ONE_SUBSCRIBE in connectedCallback.
	private readonly open$ = new Subject<MouseEvent | undefined>()
	private readonly close$ = new Subject<void>()
	private readonly announce$ = new Subject<string>()

	@state() private isOpen = false
	@state() private announceMessage = ''

	override disconnectedCallback(): void {
		super.disconnectedCallback()
		this.isOpen = false
	}

	override connectedCallback(): void {
		super.connectedCallback()

		merge(
			// Overlay session (rxjs SESSIONS_USE_HIGHER_ORDER_OBSERVABLES).
			this.open$.pipe(
				tap(() => {
					this.isOpen = true
				}),
				// Anchor comes from the tracked gesture — this session opens
				// synchronously off the trigger's click (see toggleDropdown).
				exhaustMap(() =>
					show(
						// Reactive factory — closed-over props read fresh on each mount.
						// NOTHING inside it may read a signal: the overlay evaluates it
						// inside a Signal.Computed, so a `componentText(…)` read here would
						// subscribe the mount to the wording table and re-commit `.value`
						// over the panel's own draft on the next swap. The panel reads its
						// own copy in its own render, where it is safe.
						() => html`
							<schmancy-date-field-dialog
								?data-overlay-exempt=${inExemptLayer(this)}
								.type=${this.type}
								.value=${this.value || null}
								.min=${this.min}
								.max=${this.max}
								@calendar-input=${(ev: CustomEvent<{ value: string }>) => {
									ev.stopPropagation()
									this.dispatchEvent(
										new CustomEvent('input', {
											detail: { value: ev.detail.value },
											bubbles: true,
											composed: true,
										}),
									)
								}}
								@calendar-change=${(ev: CustomEvent<{ value: string }>) => {
									ev.stopPropagation()
									this.value = ev.detail.value
									this.markTouched()
									this.announce$.next(
										componentText('dateField.announce', {
											date: formatDateInstant(this.value, this.type, this.placeholder, this.locale, this.format),
										}),
									)
									this.emitChange({ value: this.value })
									// No auto-close in either mode: the panel dispatches its own
									// structured `close` right behind this, on Save and on
									// Cancel alike, and reaching for a second one here would
									// close a surface that is already closing.
								}}
								@calendar-cancel=${(ev: Event) => {
									// Nothing is written. The stop keeps the panel's internal
									// vocabulary from leaving the field, whose own event surface
									// is `input` and `change`.
									ev.stopPropagation()
								}}
							></schmancy-date-field-dialog>
						`,
						{
							sticky: true,
							// A dialog has no name-from-content, so this is not optional. Read
							// out here rather than inside the factory — see its comment above.
							ariaLabel: this.accessibleName,
						},
					).pipe(
						takeUntil(this.close$),
						finalize(() => {
							this.isOpen = false
						}),
					),
				),
			),

			this.announce$.pipe(
				switchMap(message => concat(of(message), timer(1500).pipe(map(() => '')))),
				tap(message => {
					this.announceMessage = message
				}),
			),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe()
	}

	override willUpdate(changed: PropertyValues): void {
		super.willUpdate(changed)
		// The mixin re-validates on `value`/`required`/`disabled`; the bounds are
		// this control's own additions to the truth table.
		//
		// `this.error` keeps a DISPLAYED message re-derived on every update, which is
		// what makes it follow a `setComponentText` swap: the wording read lives inside
		// checkValidity, so an update where checkValidity doesn't run drops that
		// dependency and strands the visible message in the outgoing language.
		if (changed.has('min') || changed.has('max') || changed.has('type') || this.error) {
			this.checkValidity()
		}
	}

	override checkValidity(): boolean {
		if (this.required && !this.value) {
			return this.setFieldValidity(
				{ valueMissing: true },
				this.errorMessages?.valueMissing ?? componentText('dateField.error.valueMissing'),
			)
		}
		// Compared as real instants via dayjs (not string ordering), the same
		// parser the calendar and every other date comparison in this library use,
		// so a datetime-local value's time-of-day participates correctly.
		// The bound goes into the sentence as the raw `min`/`max` this field was
		// given, which is what it has always shown here — `<schmancy-date-range>`
		// instead formats its own bound through `formatDateInstant`, so the two
		// controls spell a bound differently in an error today. Either spelling is
		// one `{{min}}` inside a key that owns the whole sentence; reconciling them
		// changes what an English consumer reads and is not this pass's to make.
		if (this.min && this.value && dayjs(this.value).valueOf() < dayjs(this.min).valueOf()) {
			return this.setFieldValidity(
				{ rangeUnderflow: true },
				this.errorMessages?.rangeUnderflow ?? componentText('dateField.error.beforeMin', { min: this.min }),
			)
		}
		if (this.max && this.value && dayjs(this.value).valueOf() > dayjs(this.max).valueOf()) {
			return this.setFieldValidity(
				{ rangeOverflow: true },
				this.errorMessages?.rangeOverflow ?? componentText('dateField.error.afterMax', { max: this.max }),
			)
		}
		return this.setFieldValidity({})
	}

	/**
	 * Native FACE lifecycle — restore value after bfcache / form autofill. This
	 * control submits through the mixin's single-entry `setFormValue(this.value)`,
	 * so the restore state is that same string; anything else is a state some
	 * other field wrote and is left alone.
	 */
	override formStateRestoreCallback(state: string | File | FormData | null): void {
		if (typeof state !== 'string') return
		this.value = state
	}

	/**
	 * The trigger is inert while the panel is up.
	 *
	 * `sticky` never constructs `outsideClick$` (`overlay.component.ts` gates it
	 * on `!this.sticky`), so `suppressNextClick()` never arms and the trigger's
	 * click reaches this handler unimpeded — where a toggle would fire `close$`,
	 * a programmatic teardown that destroys the uncommitted draft with no exit
	 * animation and no `calendar-cancel`. The panel renders its own two exits,
	 * and Escape and Back are live besides.
	 */
	private toggleDropdown(e: MouseEvent) {
		e.stopPropagation()
		if (this.disabled || this.readonly || this.isOpen) return
		this.open$.next(e)
	}

	render() {
		const showError = this.error && !!this.validationMessage
		const display = formatDateInstant(this.value, this.type, this.placeholder, this.locale, this.format)
		// `sticky: true` announces the surface as role="dialog" at every width, and
		// it holds an uncommitted draft with its own Save and Cancel — which is
		// what "dialog" tells a screen reader is coming.
		const hasPopup = 'dialog'
		// The description joins the name rather than riding an aria-describedby:
		// the node carrying the button role lives one shadow root deeper than the
		// text, and an IDREF cannot cross that boundary, so a forwarded
		// aria-describedby would resolve to nothing. The error also stays a
		// role="alert" line below, which announces itself on appearance.
		const description = showError ? this.validationMessage : (this.hint ?? '')
		// The name and the visible label are two decisions, and only `label` makes
		// both: a bare `aria-label` names this trigger and renders nothing, which
		// is what lets several of them on one surface answer to their own names
		// under a heading that stays the loudest thing in the cell.
		const label = this.accessibleName
		// Two keys rather than one plus an appended tail: the description's join is
		// inside whichever sentence the table owns.
		const name = description
			? componentText('dateField.triggerDescribed', { label, date: display, description })
			: componentText('dateField.trigger', { label, date: display })

		return html`
			<div class="grid gap-1 ${this.disabled ? 'opacity-60 pointer-events-none' : ''}">
				<div class="sr-only" role="status" aria-live="polite" aria-atomic="true">${this.announceMessage}</div>

				${when(
					!!this.label,
					() => html`
						<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
							${this.label}
						</schmancy-typography>
					`,
				)}

				<!-- The ring rung matches schmancy-button's own rounded-xl corner, so
				the outline traces the trigger's shape instead of squaring it off.
				outline-solid is load-bearing: the bare outline utility resolves a
				width and a colour in the baked shadow-DOM sheet but emits no
				outline-style, so the ring never paints. -->
				<div
					class=${this.classMap({
						'w-fit': true,
						'rounded-xl outline-solid outline-2 outline-error-default': showError,
					})}
				>
					<schmancy-button
						class="w-full"
						variant=${this.variant}
						color=${this.color}
						type="button"
						aria-haspopup=${hasPopup}
						aria-expanded=${this.isOpen}
						aria-invalid=${showError ? 'true' : 'false'}
						aria-label=${name}
						@click=${this.toggleDropdown}
						@blur=${this.markTouched}
						?disabled=${this.disabled}
						?readonly=${this.readonly}
					>
						<span class="block min-w-0 truncate">${display}</span>
					</schmancy-button>
				</div>

				${when(
					!!description,
					() => html`
						<div
							role=${ifDefined(showError ? 'alert' : undefined)}
							class=${this.classMap({ 'text-sm wrap-anywhere': true, 'text-error-default': showError, 'text-surface-onVariant': !showError })}
						>
							${description}
						</div>
					`,
				)}
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-date-field': SchmancyDateField
	}
}
